import { useState, useContext, useEffect } from "react";

// @mui core components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import AddIcon from "@mui/icons-material/Add";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import Avatar from "@mui/material/Avatar";

// Soft UI Dashboard PRO React components
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiAvatar from "components/SuiAvatar";
import SuiBadge from "components/SuiBadge";

import toast from "react-hot-toast";
import moment from "moment";
import { useForm, Controller } from "react-hook-form";

import { storeTags } from "utils/data";
import { capitalCase } from "capital-case";
// Images
import ImgCrop from "antd-img-crop";
import { Upload } from "antd";
import { getBase64, dataURLtoFile, beforeUpload } from "utils/imageHelper";

import { StoreContext } from "context/store";
import { AccountContext } from "context/account";
import api from "services/api";
import { nanoid } from "nanoid";
import { ErrorMessage } from "@hookform/error-message";

const STORE_DESCRIPTION_MAX_LENGTH = 2000; //long description
const MISSION_STATEMENT_MAX_LENGTH = 150; //short description
const STORE_NAME_MAX_LENGTH = 50;

const UploadProfileModal = ({ handleRefresh, visible, handleOk, handleClose }) => {
  const [avatarImageUrl, setAvatarImageUrl] = useState("");
  const [longDesc, setLongDesc] = useState("");
  const [storeName, setStoreName] = useState("");

  const account = useContext(AccountContext);
  const { store, updateStore } = useContext(StoreContext);

  const {
    register,
    handleSubmit,
    trigger,
    setValue,
    watch,
    getValues,
    reset,
    formState: { errors },
    control,
  } = useForm();

  useEffect(() => {
    reset({
      name: store.name,
      story: store?.story,
      instagram_link: store?.metadata?.instagram_link,
      company_link: store?.metadata?.company_link,
      facebook_link: store?.metadata?.facebook_link,
      twitter_link: store?.metadata?.twitter_link,
    });
    setLongDesc(store?.story);
    setStoreName(store.name);
    setAvatarImageUrl(store?.metadata?.avatar);
  }, [store]);

  const handleSave = async (data) => {
    let avatarUrl = avatarImageUrl;

    if (avatarImageUrl?.startsWith("data")) {
      const file = dataURLtoFile(avatarImageUrl, nanoid());

      const uploadedImg = await api.uploads.create([file]).then(({ data }) => {
        const uploaded = data.uploads.map(({ url }) => url);
        return uploaded;
      });
      avatarUrl = uploadedImg && uploadedImg.length > 0 ? uploadedImg[0] : "";
    }

    try {
      const payload = {
        metadata: {
          avatar: avatarUrl,
          instagram_link: data["instagram_link"],
          company_link: data["company_link"],
          facebook_link: data["facebook_link"],
          twitter_link: data["twitter_link"],
        },
        name: data["name"],
        story: data["story"],
      };

      await updateStore(payload);
      handleRefresh();
      handleClose();
      toast.success("Update profile successfully!");
    } catch (err) {
      toast.error("Update profile failed!");
    }
  };

  const handleUploadImages = (data) => {
    return getBase64(data.file);
    // return api.uploads.create([file])
  };

  const handleCropImgOk = async (data) => {
    try {
      const url = await getBase64(data);
      var img = new Image();

      img.onload = resizeImage;
      img.src = url;

      function resizeImage() {
        var canvas = document.createElement("canvas");
        canvas.width = 360;
        canvas.height = 360;
        var ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, 360, 360);
        const newDataUri = canvas.toDataURL();

        setAvatarImageUrl(newDataUri);
      }
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <Dialog
      open={visible}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="md"
      fullWidth={true}
    >
      <DialogTitle id="alert-dialog-title">
        Update Profile
        <IconButton
          aria-label="close"
          onClick={() => handleClose()}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <div>
          <SuiBox component="form" onSubmit={handleSubmit(handleSave)}>
            <SuiBox pb={3} px={3}>
              <Grid container spacing={3} className="items-start">
                <Grid item xs={12} sm={12}>
                  <SuiBox
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                    height="100%"
                  >
                    <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                      <SuiTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        textTransform="capitalize"
                      >
                        Avatar
                      </SuiTypography>
                    </SuiBox>
                    <div className="flex justify-center items-center gap-2">
                      <ImgCrop rotate shape="round" onModalOk={handleCropImgOk}>
                        <Upload
                          name="avatar"
                          className="hover:cursor-pointer flex items-center ml-1"
                          showUploadList={false}
                          customRequest={handleUploadImages}
                          beforeUpload={beforeUpload}
                        >
                          <Tooltip title={"Click to change"} placement="top">
                            <Avatar
                              src={avatarImageUrl}
                              alt="profile-image"
                              variant="circle"
                              sx={{ width: 120, height: 120 }}
                              shadow="sm"
                            />
                          </Tooltip>
                        </Upload>
                      </ImgCrop>

                      {store?.metadata?.avatar != avatarImageUrl ? (
                        <SuiBox>
                          <SuiButton
                            variant="gradient"
                            color="light"
                            onClick={() => setAvatarImageUrl(store?.metadata?.avatar)}
                          >
                            reset
                          </SuiButton>
                        </SuiBox>
                      ) : null}

                      <ImgCrop rotate shape="round" onModalOk={handleCropImgOk}>
                        <Upload
                          name="avatar"
                          className="hover:cursor-pointer flex items-center"
                          showUploadList={false}
                          customRequest={handleUploadImages}
                          beforeUpload={beforeUpload}
                        >
                          <SuiButton variant="gradient" color="dark">
                            Upload
                          </SuiButton>
                        </Upload>
                      </ImgCrop>
                    </div>
                  </SuiBox>
                </Grid>
                <Grid item xs={12} sm={12}>
                  <SuiBox
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                    height="100%"
                  >
                    <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                      <SuiTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        textTransform="capitalize"
                        required
                      >
                        Company Name
                      </SuiTypography>
                    </SuiBox>
                    <SuiInput
                      placeholder="Company Name"
                      required
                      componentsProps={{ input: { maxlength: STORE_NAME_MAX_LENGTH } }}
                      {...register("name", {
                        required: true,
                        maxLength: {
                          value: STORE_NAME_MAX_LENGTH,
                          message: `Maxmium length should not exceed ${STORE_NAME_MAX_LENGTH} characters`,
                        },
                        value: storeName,
                        onChange: (e) => {
                          setStoreName(e.target.value);
                        },
                      })}
                    />
                    <div className="flex justify-between">
                      <div>
                        <ErrorMessage
                          errors={errors}
                          name="name"
                          render={({ message }) => (
                            <SuiTypography
                              variant="text"
                              fontWeight="regular"
                              fontSize="12px"
                              color="error"
                              className="ml-2"
                            >
                              {message}
                            </SuiTypography>
                          )}
                        />
                      </div>
                      <div className="text-sm text-gray-400">
                        {storeName ? storeName.length : 0}/{STORE_NAME_MAX_LENGTH}
                      </div>
                    </div>
                  </SuiBox>
                </Grid>

                <Grid item xs={12} sm={12}>
                  <SuiBox
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                    height="100%"
                  >
                    <SuiBox ml={0.5} mb={1} lineHeight={0} display="inline-block">
                      <SuiTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        textTransform="capitalize"
                      >
                        Company Description
                      </SuiTypography>
                    </SuiBox>

                    <SuiInput
                      placeholder="This is your opportunity to tell your company's story and explain what you can
                bring to the table. Be as detailed as you would like."
                      rows={6}
                      multiline={true}
                      fullWidth={true}
                      componentsProps={{ input: { maxlength: STORE_DESCRIPTION_MAX_LENGTH } }}
                      {...register(`story`, {
                        maxLength: STORE_DESCRIPTION_MAX_LENGTH,
                        value: longDesc,
                        onChange: (e) => {
                          setLongDesc(e.target.value);
                        },
                      })}
                    />
                    <div className="flex justify-between">
                      <div>
                        {errors.story?.type === "maxLength" ? (
                          <SuiTypography
                            variant="text"
                            fontWeight="regular"
                            fontSize="12px"
                            color="error"
                            className="ml-2"
                          >
                            Maxmium length should not exceed {STORE_DESCRIPTION_MAX_LENGTH}{" "}
                            characters
                          </SuiTypography>
                        ) : (
                          ""
                        )}
                      </div>
                      <div className="text-sm text-gray-400">
                        {longDesc ? longDesc.length : 0}/{STORE_DESCRIPTION_MAX_LENGTH}
                      </div>
                    </div>
                  </SuiBox>
                </Grid>
              </Grid>
            </SuiBox>

            <SuiBox pb={3} px={3}>
              <Grid container spacing={3} className="items-start">
                <Grid item xs={12} sm={6}>
                  <SuiBox
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                    height="100%"
                  >
                    <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                      <SuiTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        textTransform="capitalize"
                      >
                        Company Link
                      </SuiTypography>
                    </SuiBox>
                    <SuiInput placeholder="www.yourcomapany.com" {...register("company_link")} />
                  </SuiBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SuiBox
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                    height="100%"
                  >
                    <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                      <SuiTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        textTransform="capitalize"
                      >
                        Instagram Link
                      </SuiTypography>
                    </SuiBox>
                    <SuiInput placeholder="www.instagram.com" {...register("instagram_link")} />
                  </SuiBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SuiBox
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                    height="100%"
                  >
                    <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                      <SuiTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        textTransform="capitalize"
                      >
                        Facebook Link
                      </SuiTypography>
                    </SuiBox>
                    <SuiInput placeholder="www.facebook.com" {...register("facebook_link")} />
                  </SuiBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SuiBox
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                    height="100%"
                  >
                    <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                      <SuiTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        textTransform="capitalize"
                      >
                        Twitter Link
                      </SuiTypography>
                    </SuiBox>
                    <SuiInput placeholder="www.twitter.com" {...register("twitter_link")} />
                  </SuiBox>
                </Grid>
              </Grid>
              <SuiBox
                pt={3}
                px={3}
                display="flex"
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                flexDirection={{ xs: "column", sm: "row" }}
              >
                <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
                  <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                    <SuiButton
                      variant="gradient"
                      color="light"
                      sx={{ height: "100%" }}
                      onClick={handleClose}
                    >
                      Cancel
                    </SuiButton>
                  </SuiBox>
                </SuiBox>

                <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
                  <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                    <SuiButton
                      variant="gradient"
                      color="dark"
                      sx={{ height: "100%" }}
                      type="submit"
                    >
                      save
                    </SuiButton>
                  </SuiBox>
                </SuiBox>
              </SuiBox>
            </SuiBox>
          </SuiBox>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UploadProfileModal;
